import { Button, Grid, makeStyles, Snackbar, Typography, Checkbox, TablePagination } from '@material-ui/core';
import { getRedBook, mergeRedBook } from '../../api/redbook';
import { Alert } from '../../components/Alert/Alert';
import { ConfirmationDialog } from '../../components/Dialogs/ConfirmationDialog';
import Spinner from '../../components/Spinner';
import { MergeResult, RedBookRecord, User, UserRole } from '../../domain/domain';
import MUIDataTable from 'mui-datatables';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { ApplicationState } from '../../reducers';
import { trimString } from '../../utils';

import { compose } from 'redux';
import { connect } from 'react-redux';
import MergeResultDialog from './MergeResultDialog';
import HourglassSpinner from '../HourglassSpinner';
import NotFound from '../../components/NotFound';
import { SNACKBAR_LONG_AUTO_HIDE_DURATION_MS } from '../../api/common';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: '90%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 20
    },
    buttonDisabled: {
        color: 'rgba(0, 0, 0, 0.6)',
        borderColor: theme.palette.action.disabled,
        cursor: 'default',
        '&:hover': {
            borderColor: theme.palette.action.disabled
        },
        '&:focus': {
            borderColor: theme.palette.action.disabled
        }
    },
    searchContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
    },
    searchWrapper: {
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        padding: '0 8px',
        backgroundColor: '#fff',
        '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.87)'
        },
        '&:focus-within': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            padding: '0 7px',
        }
    },
    searchLabel: {
        display: 'block',
        marginBottom: '4px',
        color: 'rgba(0, 0, 0, 1.0)',
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.43,
    },
    searchInput: {
        border: 'none',
        outline: 'none',
        width: '100%',
        padding: '8px 0',
        fontSize: '0.875rem',
        backgroundColor: 'transparent',
    },
}));

type Props = {
    currentUser: User
}

const RedBookPageV2: React.FC<Props> = ({ currentUser }) => {
    const match = useRouteMatch();
    const classes = useStyles();
    const [records, setRecords] = useState<RedBookRecord[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [billNumber] = useState<string>((match.params as any).billNumber);
    const [billSection] = useState<string>((match.params as any).billSection);
    const [mergeConfirmationDialogOpen, setMergeConfirmationDialogOpen] = useState<boolean>(false);
    const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
    const [snackBarMessage, setSnackBarMessage] = useState<string>("");
    const [snackBarSeverity, setSnackBarSeverity] = useState<"success" | "error">("success");
    const [mergeResultDialogOpen, setMergeResultDialogOpen] = useState<boolean>(false);
    const [mergeResult, setMergeResult] = useState<MergeResult | undefined>(undefined);
    const [mergeLoading, setMergeLoading] = useState<boolean>(false);
    const [mergeWarning, setMergeWarning] = useState<string>("");

    useDocumentTitle("Red Book (Current Year Only)");

    // State to track the number of filtered rows
    const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
    const liveRegionRef = useRef<HTMLDivElement>(null);
    const [liveRegionMessage, setLiveRegionMessage] = useState<string>('');

    const sortCompare = (order: string, dataType: 'number' | 'date') => {
        return (a: any, b: any) => {
            let valA = 0, valB = 0;

            if (dataType === 'number') {
                valA = parseInt(a.data, 10) || 0;
                valB = parseInt(b.data, 10) || 0;
            } else if (dataType === 'date') {
                valA = new Date(a.data).getTime() || 0;
                valB = new Date(b.data).getTime() || 0;
            }

            return order === 'asc' ? valA - valB : valB - valA;
        };
    };

    const compareCRS = (a: string, b: string, order: string) => {
        const parseCRS = (str: string) => {
            const [title, article, sectionWithSubsection] = str.split('-');
            const section = sectionWithSubsection?.split('(')[0] || '0';

            return [
                parseInt(title, 10) || 0,
                parseInt(article, 10) || 0,
                parseInt(section, 10) || 0
            ];
        };

        const partsA = parseCRS(a);
        const partsB = parseCRS(b);

        for (let i = 0; i < partsA.length; i++) {
            if (partsA[i] !== partsB[i]) {
                return order === 'asc' ? partsA[i] - partsB[i] : partsB[i] - partsA[i];
            }
        }

        return 0;
    };

    const columns = [
        {
            name: 'crs',
            label: 'C.R.S',
            options: {
                sortCompare: (order: string) => {
                    return (a: any, b: any) => {
                        return compareCRS(a.data, b.data, order);
                    };
                },
            }
        },
        {
            name: 'change',
            label: 'Change'
        },
        {
            name: 'billNo',
            label: 'Bill No.'
        },
        {
            name: 'billSection',
            label: 'Bill §',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'footnote',
            label: 'Footnote'
        },
        {
            name: 'chapter',
            label: 'Chapter',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'page',
            label: 'Page',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'number')
            }
        },
        {
            name: 'effectiveDate',
            label: 'Effective Date',
            options: {
                sortCompare: (order: string) => sortCompare(order, 'date')
            }
        }
    ];

    // Define the CustomTableSelectCell component
    const CustomTableSelectCell = (props: any) => {
        const { onChange } = props;
        const isHeader = props['data-description'] === 'row-select-header';
        const checked = props.checked || false;
        const indeterminate = props.indeterminate || false;
        let ariaLabel = "";
        if (isHeader) {
            ariaLabel = "Select all rows";
        }
        const reduceProps = {...props}
        delete reduceProps.classes;
        const mergedInputProps = {
            ...reduceProps,
            'aria-label': ariaLabel,
            'aria-checked': checked,
        };

        return (
                <Checkbox
                    indeterminate={isHeader ? indeterminate : false}
                    checked={checked}
                    onChange={onChange}
                    inputProps={mergedInputProps}
                />
        );
    };

    const loadRecords = useCallback(() => {
        setLoading(true);
        getRedBook(trimString(billNumber), trimString(billSection)).then(response => {
            setRecords(response.data);
            // Initialize filteredRowCount with the total number of records
            setFilteredRowCount(response.data.length);
        }).catch(error => {
            console.error(error);
        }).finally(() => {
            setLoading(false);
        })
    }, [billNumber, billSection]);

    useEffect(() => {
        loadRecords();
        setTimeout(() => {
            const mergeBtn = document.getElementById('mergeBtn');
            // Fix a11y issues in Material-UI DataTable
            if (mergeBtn) {
                mergeBtn.setAttribute('tabIndex', '0');
            }
            const searchBarFormGroup = document.querySelector(".MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-fullWidth.MuiInput-fullWidth.MuiInputBase-formControl.MuiInput-formControl")
            console.log(document.querySelectorAll(".MuiInputBase-root.MuiInput-root.MuiInput-underline.MuiInputBase-fullWidth.MuiInput-fullWidth.MuiInputBase-formControl.MuiInput-formControl"))
            // Insert a visible label for the search input
            if (searchBarFormGroup) {
                console.log("searchBarFormGroup", searchBarFormGroup);
                const searchLabel = document.createElement('label');
                const searchInput = searchBarFormGroup.querySelector('input[aria-label="Search"]');
                searchInput?.setAttribute('id', 'search-input');
                searchLabel.className = classes.searchLabel;
                searchLabel.htmlFor = 'search-input';
                searchLabel.textContent = 'Search Records:';
                searchBarFormGroup.insertBefore(searchLabel, searchBarFormGroup.firstChild);
                // Move the merge button right after the search input
                searchBarFormGroup.appendChild(mergeBtn!);
            }
        }, 200)
    }, [classes.searchLabel, loadRecords, ]);

    // Effect to announce filtered results changes
    useEffect(() => {
        let message = '';
        if (loading) {
            message = 'Loading search results, please wait.';
        } else if (filteredRowCount === 0) {
            message = 'Sorry, no matching records found.';
        } else {
            message = `${filteredRowCount} result${filteredRowCount === 1 ? '' : 's'} found.`;
        }
        announceMessage(message);
    }, [filteredRowCount, loading]);
    const announceMessage = (message: string) => {
        setLiveRegionMessage('');
        setTimeout(() => {
            setLiveRegionMessage(message);
        }, 100);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            const footerTables = document.querySelector('[class*=MUIDataTableFooter]');
            if(footerTables instanceof HTMLElement){
                footerTables.setAttribute('role', 'presentation');
            }
        }, 0);
        return () => clearTimeout(timer);
    }, [records]);


    const merge = () => {
        if (selectedRows && selectedRows.length > 0) {
            const selectedRecords = selectedRows.map(rowIndex => records[rowIndex]);
            const hasMissingEffectiveDate = selectedRecords.some(record => !record.effectiveDate);

            if (hasMissingEffectiveDate) {
                setMergeWarning("Warning: Some selected rows do not have an effective date.");
            } else {
                setMergeWarning("");
            }
        }
        setMergeConfirmationDialogOpen(true);
    }

    const getHeader = () => {
        return (
            <Grid container direction='row' className="flex-gap-1">
                {(currentUser.userRole === UserRole.PUBLICATIONS_TEAM || currentUser.userRole === UserRole.ADMINISTRATOR) &&
                    <Grid item>
                        <Button id="mergeBtn" variant='outlined' disabled={selectedRows === undefined || selectedRows.length === 0}  color='primary' onClick={() => merge()}>Merge</Button>
                    </Grid>
                }
            </Grid>
        )
    }

    if (currentUser.userRole === UserRole.NON_PUBLICATIONS_TEAM) {
        return <NotFound />
    }

    return (
        <>
            {mergeResult &&
                <MergeResultDialog open={mergeResultDialogOpen} mergeResult={mergeResult} onClose={() => {
                    setMergeResultDialogOpen(false);
                }} />
            }
            <Snackbar open={snackBarOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={SNACKBAR_LONG_AUTO_HIDE_DURATION_MS} onClose={() => { setSnackBarOpen(false) }}>
                <Alert onClose={() => { setSnackBarOpen(false) }} severity={snackBarSeverity}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
            <ConfirmationDialog
                open={mergeConfirmationDialogOpen}
                title="Merge"
                loading={mergeLoading}
                loadingText={<HourglassSpinner message="Merge in progress, please wait..." />}
                contentText={`Are you sure you want to perform the merge for the ${selectedRows?.length || 0} selected row(s) in the search results?`}
                warningText={mergeWarning}
                handleNo={() => {
                    setMergeConfirmationDialogOpen(false);
                    setMergeLoading(false);
                }}
                handleYes={() => {
                    setMergeLoading(true);
                    setMergeResult(undefined);
                    let selectedRecords = selectedRows && selectedRows.length > 0 ? selectedRows.map(rowIndex => records[rowIndex]) : [];
                    mergeRedBook(billNumber, billSection, selectedRecords).then(response => {
                        setMergeResultDialogOpen(true);
                        setMergeResult(response.data);
                    }).catch(error => {
                        console.error(error);
                        setSnackBarSeverity("error");
                        const errorMessage = 'Error: Merge operation failed. Please try again later.';
                        setSnackBarMessage(errorMessage);
                        setSnackBarOpen(true);
                        setMergeLoading(false);
                    }).finally(() => {
                        setMergeLoading(false);
                        setMergeConfirmationDialogOpen(false);
                        setSelectedRows([]);
                    })
                }}
            />
            <Grid container direction='column' className={`${classes.root} flex-gap-1`}>
                <Grid item>
                    <Typography variant='h1'>Red Book (Current Year Only)</Typography>
                </Grid>
                <Grid item>
                    {getHeader()}
                </Grid>
                <Grid item>
                    <div ref={liveRegionRef}
                        aria-live="assertive"
                        aria-atomic="true"
                        className={'visually-hidden'}
                    >
                        {liveRegionMessage}
                    </div>
                    {loading &&
                        <Spinner />
                    }
                    {!loading &&
                        <MUIDataTable
                            title={"Red Book records table"}
                            columns={columns}
                            data={records}
                            options={{
                                filter: false,
                                search: true,
                                searchAlwaysOpen: true,
                                print: false,
                                download: false,
                                viewColumns: false,
                                selectableRowsHeader: true,
                                selectableRows: 'multiple',
                                selectToolbarPlacement: 'above',
                                customToolbarSelect: () => null,
                                rowsSelected: selectedRows,
                                onRowSelectionChange: (rowsSelectedData, allRows, rowsSelected) => {
                                    setSelectedRows(rowsSelected);
                                },
                                onSearchChange: (searchText: string | null) => {
                                    if (searchText === null || searchText === '') {
                                        // If search is cleared, show all records
                                        setFilteredRowCount(records.length);
                                    } else {
                                        // Filter records based on search text
                                        const filtered = records.filter(record =>
                                            Object.values(record).some(value =>
                                                value &&
                                                value.toString().toLowerCase().includes(searchText.toLowerCase())
                                            )
                                        );
                                        setFilteredRowCount(filtered.length);
                                    }
                                },
                                elevation: 0,
                                rowsPerPageOptions: [10, 50, 100, 200],
                                setTableProps: () => {
                                    return {
                                        size: "small",
                                        padding: "1px",
                                    }
                                },
                                customFooter: (
                                    count: number,
                                    page: number,
                                    rowsPerPage: number,
                                    changeRowsPerPage: (value: string | number) => void,
                                    changePage: (newPage: number) => void,
                                    textLabels: any
                                ) => {
                                    return (
                                        <TablePagination
                                            component="div"
                                            count={count}
                                            page={page}
                                            rowsPerPage={rowsPerPage}
                                            labelRowsPerPage={textLabels?.pagination?.rowsPerPage || "Rows per page:"}
                                            labelDisplayedRows={({ from, to, count: currentCount }) => {
                                                const totalPages = Math.ceil(currentCount / rowsPerPage);
                                                const currentPage = page + 1;
                                                return (
                                                    <div aria-live="polite" role="status">
                                                        <span>
                                                            {`${from}-${to} ${textLabels?.pagination?.displayRows || "of"} ${currentCount}`}
                                                        </span>
                                                        <span className={'visually-hidden'}>
                                                            Page {currentPage} of {totalPages}
                                                        </span>
                                                    </div>
                                                );
                                            }}
                                            backIconButtonProps={{
                                                'aria-label': textLabels?.pagination?.previous || "Previous Page",
                                            }}
                                            nextIconButtonProps={{
                                                'aria-label': textLabels?.pagination?.next || "Next Page",
                                            }}

                                            SelectProps={{
                                                inputProps: {
                                                    'aria-label': `Rows per page, currently set to ${rowsPerPage} rows, on page ${page + 1} of ${Math.ceil(count / rowsPerPage)}, select number of rows per page`,
                                                },
                                            }}
                                            onPageChange={(_, newPage) => changePage(newPage)}
                                            onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                changeRowsPerPage(parseInt(event.target.value, 10));
                                            }}
                                        />
                                    );
                                }
                            }}
                            components={{
                                Checkbox: CustomTableSelectCell
                            }}
                        />
                    }
                </Grid>
            </Grid>
        </>
    )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentUser: state.user.currentUser
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return { dispatch };
}

export default compose<any>(
    connect(mapStateToProps, mapDispatchToProps)
)(RedBookPageV2);
